import type { AppMode } from '@iheartjane/types';
import { createExternalStore, Storage } from '@iheartjane/utils';
import { useSyncExternalStore } from 'react';

type LegacyCartUuidRead = (
  appMode: AppMode,
  storeId?: string | number
) => string | null;

type LegacyCartUuidClear = (
  appMode: AppMode,
  storeId?: string | number
) => void;

type CartUuidStorageKey = (
  appMode: AppMode,
  storeId?: string | number
) => string;

/**
 * App-mode-scoped only. Kiosk depends on this default — `cart-uuid-kiosk` has
 * live writers — so a consumer needing store scoping injects `storageKey`.
 */
const defaultCartUuidStorageKey: CartUuidStorageKey = (appMode) =>
  `cart-uuid-${appMode}`;

// TODO(carts-v3): once the legacy reads go, move kiosk onto `localCartUuid.ts`
// (reading `cart-uuid-kiosk` once) and fold this factory into it.
export const createLocalCartUuidStore = ({
  legacyClear,
  legacyRead,
  storageKey: cartUuidStorageKey = defaultCartUuidStorageKey,
}: {
  legacyClear?: LegacyCartUuidClear;
  legacyRead?: LegacyCartUuidRead;
  storageKey?: CartUuidStorageKey;
} = {}) => {
  const { notify, subscribe } = createExternalStore();

  const getLocalCartUuid = (
    appMode: AppMode,
    storeId?: string | number
  ): string | null =>
    Storage.get<string>(cartUuidStorageKey(appMode, storeId)) ||
    legacyRead?.(appMode, storeId) ||
    null;

  /** `storeId` mirrors the getter's position so a scoped key round-trips. */
  const updateLocalCartUuid = (
    appMode: AppMode,
    storeId: string | number | undefined,
    cartUuid: string | null
  ) => {
    const key = cartUuidStorageKey(appMode, storeId);

    if (cartUuid) {
      Storage.set(key, cartUuid);
    } else {
      Storage.remove(key);
      // Reads fall back to the legacy key, so a clear must remove it too.
      // TODO(carts-v3): remove with the legacy reads.
      legacyClear?.(appMode, storeId);
    }

    notify();
  };

  // `enabled` gates the read, not the hook: `getSnapshot` hits localStorage.
  const useLocalCartUuid = (
    appMode: AppMode,
    storeId?: string | number,
    enabled = true
  ) =>
    useSyncExternalStore(
      subscribe,
      () => (enabled ? getLocalCartUuid(appMode, storeId) : null),
      () => null
    );

  return { getLocalCartUuid, updateLocalCartUuid, useLocalCartUuid };
};
